import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import './InterpretationsPanel.css';
import { submitInterpretationRating, fetchRatingSummary } from '../../services/api';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useAuth } from '../../contexts/AuthContext';
import { usePayment } from '../../hooks/usePayment';
import ReportModal from '../ReportModal/ReportModal';
import Modal from '../Modal/Modal';
import { Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SUBSCRIPTION_PLANS } from '../../const/subscriptionPlan';
import { isCreditTopUpPlan } from '../../const/creditTopUpPacks';
import { toast } from 'react-hot-toast';

const TABS = [
  { id: 'planets', label: 'Planetary Position' },
  { id: 'majorAspects', label: 'Major Aspects' },
  { id: 'dwarf', label: 'Dwarf Planets' },
  { id: 'minorAspects', label: 'Minor Aspects' },
  { id: 'synthesis', label: 'Overall Chart Interpretation' },
  { id: 'transits', label: 'Transit' },
];

// Tabs that require a generated chart before they can be accessed
// const TABS_REQUIRE_CHART = new Set(['dwarf', 'majorAspects', 'minorAspects', 'synthesis', 'transits']);
const TABS_REQUIRE_CHART = new Set([]);

const REGULAR_PLANETS = ['mercury', 'venus', 'earth', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto'];
const EXCLUDED_PLANETS = new Set(['sun']);

// Libraries array must be static to avoid LoadScript reload warning
const LIBRARIES = ['places'];

const parseMarkdownHeadings = (content) => {
  // Support both ## and ### style headings
  const headingRegex = /^#{2,6}\s*([^\n]+)\s*/gm;
  const sections = [];
  let match;
  let currentHeading = null;
  let bodyStartIndex = 0;

  while ((match = headingRegex.exec(content)) !== null) {
    if (currentHeading) {
      const body = content.slice(bodyStartIndex, match.index).trim();
      if (body) {
        sections.push({ heading: currentHeading, body });
      }
    }
    currentHeading = match[1]?.trim();
    bodyStartIndex = headingRegex.lastIndex;
  }

  if (currentHeading) {
    const body = content.slice(bodyStartIndex).trim();
    if (body) {
      sections.push({ heading: currentHeading, body });
    }
  }

  return sections.length ? sections : null;
};

const parseBoldSections = (content) => {
  const tokens = content.split(/\*\*(.+?)\*\*/);
  if (tokens.length <= 1) {
    return null;
  }

  const sections = [];
  for (let i = 1; i < tokens.length; i += 2) {
    const heading = tokens[i]?.trim();
    const body = tokens[i + 1]?.trim();
    if (heading && body) {
      sections.push({ heading, body });
    }
  }

  return sections.length ? sections : null;
};

const stripWordCount = (heading = '') =>
  heading.replace(/\(\s*\d+\s*words?\s*\)/gi, '').trim();

const parseStructuredSections = (content) => {
  if (!content || typeof content !== 'string') {
    return null;
  }

  const sections = parseMarkdownHeadings(content) || parseBoldSections(content);
  if (!sections) {
    return null;
  }

  return sections.map((section) => ({
    heading: stripWordCount(section.heading),
    body: section.body,
  }));
};

const createEmptyPartner = () => ({
  name: '',
  birthDate: '',
  birthTime: '',
  location: '',
  timezone: '',
});

const getTodayIso = () => new Date().toISOString().split('T')[0];

const formatDateInput = (dateObj) => {
  if (!dateObj) return '';
  const year = String(dateObj.year ?? '').padStart(4, '0');
  const month = String(dateObj.month ?? '').padStart(2, '0');
  const day = String(dateObj.day ?? '').padStart(2, '0');
  if (!year.trim() || !month.trim() || !day.trim()) return '';
  return `${year}-${month}-${day}`;
};

const formatTimeInput = (timeObj) => {
  if (!timeObj) return '';
  const hour = String(timeObj.hour ?? '').padStart(2, '0');
  const minute = String(timeObj.minute ?? '').padStart(2, '0');
  if (!hour.trim() || !minute.trim()) return '';
  return `${hour}:${minute}`;
};

const buildPartnerFromBirthData = (birthData, fallbackName) => ({
  name: fallbackName || 'Current Person',
  birthDate: formatDateInput(birthData?.date),
  birthTime: formatTimeInput(birthData?.time),
  location: birthData?.location?.name || '',
  timezone: birthData?.timezone || '',
});

const timezoneStringToMinutes = (value) => {
  if (!value || typeof value !== 'string') return null;
  const sanitized = value.trim().replace(/^UTC/i, '');
  const match = sanitized.match(/([+-])(\d{2}):(\d{2})/);
  if (!match) return null;
  const sign = match[1] === '-' ? -1 : 1;
  const hours = Number(match[2]);
  const minutes = Number(match[3]);
  return sign * (hours * 60 + minutes);
};

const getDateFromInput = (value) => {
  if (!value) return null;
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) return null;
  return new Date(timestamp);
};

const getYearDiff = (dateA, dateB) => {
  if (!dateA || !dateB) return null;
  const diffMs = Math.abs(dateA.getTime() - dateB.getTime());
  if (!Number.isFinite(diffMs)) return null;
  return diffMs / (1000 * 60 * 60 * 24 * 365.25);
};

const buildSynastryKeyAspectsAuto = (partnerA, partnerB) => {
  const aspects = [];
  const basePlanetsA = [partnerA.name?.trim() || 'Partner A'];
  const basePlanetsB = [partnerB.name?.trim() || 'Partner B'];

  const addAspect = (aspect, orb = 1) => {
    aspects.push({
      planetsA: basePlanetsA,
      aspect,
      planetsB: basePlanetsB,
      orb: Math.max(0.5, Number.isFinite(orb) ? orb : 1),
    });
  };

  const yearDiff = getYearDiff(getDateFromInput(partnerA.birthDate), getDateFromInput(partnerB.birthDate));
  if (Number.isFinite(yearDiff)) {
    let aspectType = 'conjunction';
    if (yearDiff >= 1 && yearDiff < 5) aspectType = 'trine';
    else if (yearDiff >= 5 && yearDiff < 10) aspectType = 'square';
    else if (yearDiff >= 10) aspectType = 'opposition';
    const orb = Math.max(0.5, Math.min(3, (yearDiff % 5) || 1));
    addAspect(aspectType, orb);
  }

  const tzMinutesA = timezoneStringToMinutes(partnerA.timezone);
  const tzMinutesB = timezoneStringToMinutes(partnerB.timezone);
  if (Number.isFinite(tzMinutesA) && Number.isFinite(tzMinutesB)) {
    const difference = Math.abs(tzMinutesA - tzMinutesB);
    const aspectType = difference <= 120 ? 'sextile' : difference <= 240 ? 'square' : 'opposition';
    addAspect(aspectType, Math.max(0.5, Math.min(3, difference / 60 || 1)));
  }

  if (!aspects.length) {
    addAspect('conjunction', 1);
  }

  return aspects;
};

const buildTransitDescriptionFromInterpretations = (interpretations) => {
  if (!interpretations) {
    return 'Transit insight request using current heliocentric chart context.';
  }

  const sections = [];
  const addEntries = (label, entries, formatter) => {
    if (!entries?.length) return;
    const lines = entries.slice(0, 5).map(formatter);
    sections.push(`${label}:\n${lines.join('\n')}`);
  };

  addEntries(
    'Planetary positions',
    interpretations.planets,
    (entry) => `- ${entry.title || entry.entity || ''}: ${entry.content?.slice(0, 160)}...`,
  );

  addEntries(
    'Key aspects',
    interpretations.aspects,
    (entry) => `- ${entry.title || entry.entity || ''}: ${entry.content?.slice(0, 160)}...`,
  );

  if (!sections.length) {
    return 'Transit insight request using current heliocentric chart context.';
  }

  return sections.join('\n\n');
};

const InterpretationCard = ({ item, index }) => {
  const content = item?.content || '';

  // Try to parse as JSON first (to handle detailed batch interpretations from report cache)
  let parsedSections = null;
  try {
    const json = JSON.parse(content);
    // Check if it has the expected structure (position, soul, practical)
    if (json && (json.position || json.soul || json.practical || json['Position & Sign'])) {
      parsedSections = [
        { heading: 'Position & Sign', body: json.position || json['Position & Sign'] },
        { heading: 'Soul Meaning', body: json.soul || json.soul_meaning || json['Soul Meaning'] },
        { heading: 'Practical Application', body: json.practical || json.practical_application || json['Practical Application'] }
      ].filter(s => s.body); // Only keep sections with content
    } else if (json && json.interpretation) {
      // Handle aspect/transit format
      parsedSections = [
        { heading: 'Interpretation', body: json.interpretation }
      ];
    }
  } catch (e) {
    // Not JSON, ignore
  }

  const sections = parsedSections || parseStructuredSections(content);

  // Check if this is an aspect interpretation (has type property)
  const isAspect = item.type !== undefined;
  const displayTitle = item.title.length > 50 ? 'Transit Interpretation' : item.title;

  // Get the correct number of stars based on strength
  let starsPrefix = '';
  if (isAspect && item.strength?.id) {
    const strengthId = item.strength.id;
    if (strengthId === 'very_strong') {
      starsPrefix = '⭐⭐⭐ ';
    } else if (strengthId === 'strong') {
      starsPrefix = '⭐⭐ ';
    } else if (strengthId === 'moderate') {
      starsPrefix = '⭐ ';
    }
  }

  const titleWithStar = isAspect ? `${starsPrefix}${displayTitle}` : displayTitle;

  return (
    <div className={`interpretation-card ${index !== 0 ? 'mt-8' : ''}`}>
      <h5 className="interpretation-card__title font-bold">
        {titleWithStar}
      </h5>
      {sections ? (
        <div className="interpretations-sections">
          {sections.map((section) => (
            <div key={section.heading} className="interpretations-section">
              <h6>{section.heading}</h6>
              <p>{section.body}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="interpretation-card__body">
          {content}
        </p>
      )}
    </div>
  );
};

InterpretationCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
  }).isRequired,
};

const RoundLoader = ({ message }) => (
  <div className="interpretations-round-loader">
    <div className="interpretations-round-loader__spinner" />
    {message && <p className="interpretations-round-loader__message">{message}</p>}
  </div>
);

RoundLoader.propTypes = {
  message: PropTypes.string,
};

RoundLoader.defaultProps = {
  message: '',
};

const InterpretationAccordion = ({ items, emptyMessage, loading, loadingMessage }) => {
  if (loading && !items?.length) {
    return (
      <div className="interpretations-list">
        <RoundLoader message={loadingMessage || 'Loading interpretation...'} />
      </div>
    );
  }

  if (!items?.length) {
    return <p className="interpretations-empty">{emptyMessage}</p>;
  }

  return (
    <div className="interpretations-list">
      {items.map((item, index) => (
        <InterpretationCard key={item.id || item.title} item={item} index={index} />
      ))}
      {loading && (
        <div className="mt-4 flex justify-center">
          <RoundLoader message={loadingMessage || 'Loading next interpretation...'} />
        </div>
      )}
    </div>
  );
};

InterpretationAccordion.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      wordCount: PropTypes.number,
      provider: PropTypes.string,
      model: PropTypes.string,
    }),
  ),
  emptyMessage: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  loadingMessage: PropTypes.string,
};

InterpretationAccordion.defaultProps = {
  items: undefined,
  loading: false,
  loadingMessage: '',
};

const InterpretationsPanel = ({
  interpretations,
  loadingTabs,
  loadedTabs,
  chartData,
  onGeneratePlanetary,
  onGenerateAspects,
  onGenerateSynthesis,
  onGenerateTransit,
  onGenerateSynastry,
  error,
  hasChart,
  activeTab,
  setActiveTab,
}) => {
  const [isBirthDataErrorModalOpen, setIsBirthDataErrorModalOpen] = useState(false);
  const [ratingScore, setRatingScore] = useState(5);
  const [ratingComment, setRatingComment] = useState('');
  const [ratingSummary, setRatingSummary] = useState({ average: 0, count: 0 });
  const [submittingRating, setSubmittingRating] = useState(false);
  const todayIso = useMemo(() => getTodayIso(), []);
  const maxTransitDateIso = useMemo(() => {
    const today = new Date();
    const max = new Date(today);
    max.setMonth(max.getMonth() + 3);
    return max.toISOString().split('T')[0];
  }, []);
  const [transitForm, setTransitForm] = useState({
    mode: 'current',
    date: todayIso,
    startDate: todayIso,
    endDate: todayIso,
  });
  const [transitFormError, setTransitFormError] = useState('');
  const [transitFormSuccess, setTransitFormSuccess] = useState('');
  const [submittingTransit, setSubmittingTransit] = useState(false);
  const [synastryForm, setSynastryForm] = useState({
    partnerA: createEmptyPartner(),
    partnerB: createEmptyPartner(),
  });
  const [synastryFormError, setSynastryFormError] = useState('');
  const [synastryFormSuccess, setSynastryFormSuccess] = useState('');
  const [submittingSynastry, setSubmittingSynastry] = useState(false);
  const [useCurrentPersonAsPartnerA, setUseCurrentPersonAsPartnerA] = useState(false);
  const currentBirthData = chartData?.birthData;
  const hasCurrentBirthData = Boolean(currentBirthData?.date && currentBirthData?.time);
  const partnerAutocompleteRefs = useRef({});
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded: isPlacesLoaded } = useLoadScript({
    googleMapsApiKey,
    libraries: LIBRARIES,
  });

  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { initiatePayment } = usePayment();
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const handleReportPurchase = async (planType, purchaseDetails) => {
    if (!user) {
      toast.error("Please log in to purchase.");
      return;
    }

    try {
      const payload = {
        user_id: user._id || user.id,
        planType,
        addons: purchaseDetails.addons,
        dates: purchaseDetails.dates,
      };

      if (!isCreditTopUpPlan(planType)) {
        const { date, time, location, timezone, preparingFor } = chartData?.birthData || {};
        if (!date || !time) {
          console.error("Missing birth data for report purchase", chartData);
          toast.error("Unable to proceed: Chart data is incomplete. Please regenerate the chart.");
          return;
        }

        const formattedDate = `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
        const formattedTime = `${String(time.hour).padStart(2, '0')}:${String(time.minute).padStart(2, '0')}`;

        payload.birthData = {
          date: formattedDate,
          time: formattedTime,
          location: location,
          timezone: timezone,
        };
        payload.preparingFor = preparingFor;
      }

      await initiatePayment(payload, { redirect: true });
      await refreshUser();
    } catch (error) {
      if (error?.code === 'INSUFFICIENT_CREDITS') {
        toast.error('Insufficient credits. Purchase a top-up pack to continue.');
        navigate('/plans');
      } else {
        console.error("Payment initiation failed:", error);
      }
    }

    setIsReportModalOpen(false);
  };

  useEffect(() => {
    if (!hasCurrentBirthData) {
      setUseCurrentPersonAsPartnerA(false);
    }
  }, [hasCurrentBirthData]);

  useEffect(() => {
    if (useCurrentPersonAsPartnerA && hasCurrentBirthData) {
      setSynastryForm((prev) => ({
        ...prev,
        partnerA: {
          ...prev.partnerA,
          ...buildPartnerFromBirthData(currentBirthData, prev.partnerA.name || 'Current Person'),
        },
      }));
    }
  }, [useCurrentPersonAsPartnerA, hasCurrentBirthData, currentBirthData]);

  const handlePartnerPlaceChanged = (partnerKey) => {
    const autocomplete = partnerAutocompleteRefs.current[partnerKey];
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    if (!place) return;
    const description = place.formatted_address || place.name || '';
    if (!description) return;
    setSynastryForm((prev) => ({
      ...prev,
      [partnerKey]: {
        ...prev[partnerKey],
        location: description,
      },
    }));
  };

  const tabContent = useMemo(() => {
    if (!interpretations) return {};

    // Combine all planets (regular + dwarf) into single array
    const allPlanets = (interpretations.planets || [])
      .map((entry) => ({
        id: entry.id || entry.entity,
        title: entry.title || entry.entity || entry.planet,
        content: entry.content,
        wordCount: entry.wordCount,
        provider: entry.provider,
        model: entry.model,
        name: (entry.title || entry.entity || entry.planet || '').toLowerCase(),
      }))
      .filter((entry) => !EXCLUDED_PLANETS.has(entry.name));

    const regularPlanets = allPlanets.filter(p => REGULAR_PLANETS.includes(p.name));
    const dwarfPlanets = allPlanets.filter(p => !REGULAR_PLANETS.includes(p.name));

    // Process aspects
    const allAspects = (interpretations.aspects || []).map((entry) => ({
      id: entry.id || entry.entity,
      title: entry.title || entry.entity,
      content: entry.content,
      wordCount: entry.wordCount,
      provider: entry.provider,
      model: entry.model,
      type: entry.type,
      strength: entry.strength,
    }));
    const majorAspects = allAspects.filter(a => a.type !== 'minor');
    const minorAspects = allAspects.filter(a => a.type === 'minor');

    return {
      planets: regularPlanets,
      dwarf: dwarfPlanets,
      majorAspects,
      minorAspects,
      synthesis: interpretations.synthesis
        ? [
          {
            id: 'synthesis',
            title: 'Overall Chart Interpretation',
            content: interpretations.synthesis.content || interpretations.synthesis,
            wordCount: interpretations.synthesis.wordCount,
            provider: interpretations.synthesis.provider,
            model: interpretations.synthesis.model,
          },
        ]
        : [],
      transits: (interpretations.transits || []).map((entry) => ({
        id: entry.id || entry.entity,
        title: entry.title || entry.entity,
        content: entry.content,
        wordCount: entry.wordCount,
        provider: entry.provider,
        model: entry.model,
      })),
      synastry: interpretations.synastry
        ? [
          {
            id: interpretations.synastry.id || 'synastry',
            title: interpretations.synastry.title || 'Synastry Insights',
            content: interpretations.synastry.content || interpretations.synastry,
            wordCount: interpretations.synastry.wordCount,
            provider: interpretations.synastry.provider,
            model: interpretations.synastry.model,
          },
        ]
        : [],
    };
  }, [interpretations]);

  const handleTransitFieldChange = (field, value) => {
    // Auto-calculate the other date for range mode
    if (transitForm.mode === 'range' && (field === 'startDate' || field === 'endDate')) {
      const dateObj = new Date(value);
      if (!isNaN(dateObj.getTime())) {
        if (field === 'startDate') {
          // Calculate end date as 3 months from start date
          const endDateObj = new Date(dateObj);
          endDateObj.setMonth(endDateObj.getMonth() + 3);
          const endDateIso = endDateObj.toISOString().split('T')[0];
          setTransitForm((prev) => ({
            ...prev,
            startDate: value,
            endDate: endDateIso,
          }));
        } else if (field === 'endDate') {
          // Calculate start date as 3 months before end date
          const startDateObj = new Date(dateObj);
          startDateObj.setMonth(startDateObj.getMonth() - 3);
          const startDateIso = startDateObj.toISOString().split('T')[0];
          setTransitForm((prev) => ({
            ...prev,
            startDate: startDateIso,
            endDate: value,
          }));
        }
      }
    } else {
      setTransitForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
    setTransitFormError('');
    setTransitFormSuccess('');
  };

  const handleTransitModeChange = (value) => {
    setTransitForm((prev) => ({
      ...prev,
      mode: value,
      date: value === 'current' && !prev.date ? todayIso : prev.date,
      startDate: prev.startDate || todayIso,
      endDate: prev.endDate || todayIso,
    }));
    setTransitFormError('');
    setTransitFormSuccess('');
  };

  const validateTransitForm = () => {
    if (transitForm.mode === 'single' && !transitForm.date) {
      return 'Select a date for the transit.';
    }
    if (transitForm.mode === 'range') {
      if (!transitForm.startDate || !transitForm.endDate) {
        return 'Select a start or end date for the 3-month range.';
      }
    }

    if (!chartData) {
      return 'Generate a chart before requesting transit interpretations.';
    }
    if (
      !interpretations?.planets?.length &&
      !interpretations?.aspects?.length
    ) {
      return 'Generate at least one planetary or aspect interpretation before requesting a transit.';
    }
    return null;
  };

  const buildTransitPayload = () => {
    const description = buildTransitDescriptionFromInterpretations(interpretations);
    const baseDate = transitForm.date || todayIso;
    if (transitForm.mode === 'range') {
      return {
        description,
        timing: {
          start: transitForm.startDate,
          end: transitForm.endDate,
          exact: transitForm.startDate,
        },
      };
    }
    const targetDate = transitForm.mode === 'current' ? todayIso : baseDate;
    return {
      description,
      timing: {
        start: targetDate,
        end: targetDate,
        exact: targetDate,
      },
    };
  };

  const handleTransitSubmit = async (event) => {
    event.preventDefault();
    if (!onGenerateTransit) return;

    if (user?.planTier !== 'premium') {
      setIsReportModalOpen(true);
      return;
    }

    const validationError = validateTransitForm();
    if (validationError) {
      setTransitFormError(validationError);
      setTransitFormSuccess('');
      return;
    }

    setTransitFormError('');
    setTransitFormSuccess('');
    setSubmittingTransit(true);

    try {
      const payload = buildTransitPayload();
      await onGenerateTransit(chartData, payload);
    } catch (err) {
      setTransitFormError(err?.message || 'Failed to request transit interpretation.');
    } finally {
      setSubmittingTransit(false);
    }
  };

  const handlePartnerFieldChange = (partnerKey, field, value) => {
    setSynastryForm((prev) => ({
      ...prev,
      [partnerKey]: {
        ...prev[partnerKey],
        [field]: value,
      },
    }));
    setSynastryFormError('');
    setSynastryFormSuccess('');
  };

  const validateSynastryForm = () => {
    const requiredPartners = ['partnerA', 'partnerB'];
    for (const key of requiredPartners) {
      const partner = synastryForm[key];
      const label = key === 'partnerA' ? 'first partner' : 'second partner';
      if (!partner.name.trim()) {
        return `Please enter the ${label}'s name.`;
      }
      if (!partner.birthDate) {
        return `Please select the ${label}'s birth date.`;
      }
      if (!partner.birthTime) {
        return `Please provide the ${label}'s birth time.`;
      }
      if (!partner.location.trim()) {
        return `Please enter the ${label}'s birth location.`;
      }
    }
    return null;
  };

  const buildSynastryOverview = () => {
    const describePartner = (partner, label) => {
      const parts = [];
      if (partner.name) parts.push(`${label}: ${partner.name.trim()}`);
      if (partner.birthDate) parts.push(`born ${partner.birthDate}`);
      if (partner.birthTime) parts.push(`at ${partner.birthTime}`);
      if (partner.location) parts.push(`in ${partner.location.trim()}`);
      if (partner.timezone) parts.push(`UTC${partner.timezone}`);
      return parts.join(' ');
    };

    const timezoneNote = (() => {
      const tzA = synastryForm.partnerA.timezone;
      const tzB = synastryForm.partnerB.timezone;
      if (!tzA || !tzB) return null;
      const diff = timezoneStringToMinutes(tzA) - timezoneStringToMinutes(tzB);
      if (!Number.isFinite(diff) || diff === 0) {
        return `Shared circadian rhythm anchored around UTC${tzA}.`;
      }
      const hours = Math.round(Math.abs(diff) / 60);
      return `Approximate timezone gap: ${hours} hour${hours === 1 ? '' : 's'}, shaping perspective and pacing.`;
    })();

    const overviewSegments = [
      describePartner(synastryForm.partnerA, 'Partner A'),
      describePartner(synastryForm.partnerB, 'Partner B'),
      timezoneNote,
    ];

    return overviewSegments.filter(Boolean).join('\n');
  };

  const handleSynastrySubmit = async (event) => {
    event.preventDefault();
    if (!onGenerateSynastry) return;

    const validationError = validateSynastryForm();
    if (validationError) {
      setSynastryFormError(validationError);
      setSynastryFormSuccess('');
      return;
    }

    setSynastryFormError('');
    setSynastryFormSuccess('');
    setSubmittingSynastry(true);

    try {
      const payload = {
        overview: buildSynastryOverview(),
        keyAspects: buildSynastryKeyAspectsAuto(synastryForm.partnerA, synastryForm.partnerB),
        partners: {
          partnerA: synastryForm.partnerA,
          partnerB: synastryForm.partnerB,
        },
        notes: 'Auto-generated from provided birth data.',
      };
      await onGenerateSynastry(payload);
      setSynastryFormSuccess('Synastry interpretation requested. It will appear below shortly.');
    } catch (err) {
      setSynastryFormError(err?.message || 'Failed to request synastry interpretation.');
    } finally {
      setSubmittingSynastry(false);
    }
  };

  // Handle tab click - enforce chart requirement for advanced tabs and fetch interpretation if needed
  const handleTabClick = (tabId) => {
    if (!hasChart && TABS_REQUIRE_CHART.has(tabId)) {
      setIsBirthDataErrorModalOpen(true);

      const birthDataForm = document.getElementById('birth-data-form');
      if (birthDataForm) {
        birthDataForm.scrollIntoView({ behavior: 'smooth' });
      }

      return;
    }

    setActiveTab(tabId);

    if (!hasChart || !chartData) return;

    // Fetch interpretation for the clicked tab if not already loaded and not currently loading
    if (!loadedTabs[tabId] && !loadingTabs[tabId]) {
      switch (tabId) {
        case 'planets':
          if (!loadedTabs.planets && !loadingTabs.planets) {
            onGeneratePlanetary?.(chartData, 'regular');
          }
          break;
        case 'dwarf':
          if (!loadedTabs.dwarf && !loadingTabs.dwarf) {
            if (user?.planTier === 'premium') {
              onGeneratePlanetary?.(chartData, 'dwarf');
            }
          }
          break;
        case 'majorAspects':
          if (!loadedTabs.majorAspects && !loadingTabs.majorAspects) {
            onGenerateAspects?.(chartData, 'major');
          }
          break;
        case 'minorAspects':
          if (!loadedTabs.minorAspects && !loadingTabs.minorAspects) {
            if (user?.planTier === 'premium') {
              onGenerateAspects?.(chartData, 'minor');
            }
          }
          break;
        case 'synthesis':
          if (user?.planTier === 'premium') {
            onGenerateSynthesis?.(chartData);
          }
          break;
        case 'transits':
          // Transit requires a payload, so skip auto-fetch for now
          // User will need to provide transit data
          break;
        default:
          break;
      }
    }
  };

  useEffect(() => {
    const loadRatingSummary = async () => {
      if (activeTab !== 'synthesis' || !interpretations?.chartHash) {
        setRatingSummary({ average: 0, count: 0 });
        return;
      }
      try {
        const summary = await fetchRatingSummary({
          chartHash: interpretations.chartHash,
          scope: 'synthesis',
        });
        setRatingSummary(summary);
      } catch (err) {
        setRatingSummary({ average: 0, count: 0 });
      }
    };

    loadRatingSummary();
  }, [activeTab, interpretations?.chartHash]);

  const handleRatingSubmit = async (event) => {
    event.preventDefault();
    if (!interpretations?.chartHash || !tabContent.synthesis?.length) return;

    setSubmittingRating(true);
    try {
      const payload = {
        chartHash: interpretations.chartHash,
        scope: 'synthesis',
        entity: 'synthesis',
        rating: Number(ratingScore),
        comment: ratingComment,
        provider: tabContent.synthesis[0]?.provider,
        model: tabContent.synthesis[0]?.model,
      };
      const result = await submitInterpretationRating(payload);
      setRatingSummary(result.summary);
      setRatingComment('');
    } catch (err) {
      // no-op for now
    } finally {
      setSubmittingRating(false);
    }
  };

  const renderRatingPanel = () => {
    if (activeTab !== 'synthesis' || !interpretations?.chartHash || !tabContent.synthesis?.length) {
      return null;
    }

    return (
      <div className="rating-panel">
        <div className="rating-panel__summary">
          <div>
            <span>Average rating</span>
            <strong>{(ratingSummary?.average ?? 0).toFixed(1)}</strong>
          </div>
          <small>
            {ratingSummary?.count || 0} {ratingSummary?.count === 1 ? 'review' : 'reviews'}
          </small>
        </div>
        <form className="rating-panel__form" onSubmit={handleRatingSubmit}>
          <label className="rating-panel__field">
            Score
            <select value={ratingScore} onChange={(event) => setRatingScore(event.target.value)}>
              {[5, 4, 3, 2, 1].map((score) => (
                <option key={score} value={score}>
                  {score}
                </option>
              ))}
            </select>
          </label>
          <label className="rating-panel__field">
            Comment
            <textarea
              rows={2}
              value={ratingComment}
              onChange={(event) => setRatingComment(event.target.value)}
              placeholder="What resonated with you?"
            />
          </label>
          <button type="submit" disabled={submittingRating}>
            {submittingRating ? 'Submitting…' : 'Send Feedback'}
          </button>
        </form>
      </div>
    );
  };

  const renderTransitForm = () => (
    <form className="interpretations-form" onSubmit={handleTransitSubmit}>
      <div className="interpretations-form__header">
        <div>
          <h4>Request Transit Interpretation</h4>
          <p>Select the timing for this transit insight. Current chart context is applied automatically.</p>
        </div>
        <button
          type="submit"
          className="btn-generate-interpretation"
          disabled={!hasChart || submittingTransit}
        >
          {submittingTransit ? 'Requesting…' : 'Generate Transit Insight'}
        </button>
      </div>
      {/* {!hasChart && <p className="form-help">Generate a chart before requesting transit insights.</p>}
      <p className="form-help">
        The will automatically use all current planetary and aspect interpretations as context—no manual description needed.
      </p> */}
      <div className="interpretations-form__row">
        <label className="interpretations-form__field">
          Date selection
          <select
            value={transitForm.mode}
            onChange={(event) => handleTransitModeChange(event.target.value)}
          >
            <option value="current">Use current date</option>
            <option value="single">Select specific date</option>
            <option value="range">Select date range</option>
          </select>
        </label>
        {transitForm.mode === 'single' && (
          <label className="interpretations-form__field">
            Date
            <input
              type="date"
              value={transitForm.date}
              // min={todayIso}
              // max={maxTransitDateIso}
              onChange={(event) => handleTransitFieldChange('date', event.target.value)}
            />
          </label>
        )}
        {transitForm.mode === 'range' && (
          <>
            <label className="interpretations-form__field">
              Start date
              <input
                type="date"
                value={transitForm.startDate}
                onChange={(event) => handleTransitFieldChange('startDate', event.target.value)}
              />
              {/* <small style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
                End date will auto-adjust to 3 months later
              </small> */}
            </label>
            <label className="interpretations-form__field">
              End date
              <input
                type="date"
                value={transitForm.endDate}
                onChange={(event) => handleTransitFieldChange('endDate', event.target.value)}
              />
              {/* <small style={{ display: 'block', marginTop: '0.25rem', fontSize: '0.85rem', color: '#666' }}>
                Start date will auto-adjust to 3 months earlier
              </small> */}
            </label>
          </>
        )}
      </div>
      {transitFormError && <p className="form-error">{transitFormError}</p>}
      {/* {transitFormSuccess && <p className="form-success">{transitFormSuccess}</p>} */}
    </form>
  );

  const renderSynastryForm = () => (
    <form className="interpretations-form interpretations-form--synastry" onSubmit={handleSynastrySubmit}>
      <div className="interpretations-form__header">
        <div>
          <h4>Request Synastry Interpretation</h4>
          <p>Provide birth details for both partners; relationship context and key aspects are auto-generated.</p>
        </div>
        <button
          type="submit"
          className="btn-generate-interpretation"
          disabled={submittingSynastry}
        >
          {submittingSynastry ? 'Requesting…' : 'Generate Synastry Insight'}
        </button>
      </div>
      <div className="interpretations-form__grid interpretations-form__grid--synastry">
        {['partnerA', 'partnerB'].map((partnerKey, index) => {
          const partner = synastryForm[partnerKey];
          const disableAutoFields = useCurrentPersonAsPartnerA && partnerKey === 'partnerA';
          return (
            <div className="interpretations-form__card" key={partnerKey}>
              <div className="interpretations-form__card-header">
                <h5>{index === 0 ? 'Partner A' : 'Partner B'}</h5>
                {partnerKey === 'partnerA' && (
                  <label className="interpretations-form__field interpretations-form__field--checkbox">
                    <input
                      type="checkbox"
                      checked={useCurrentPersonAsPartnerA}
                      onChange={(event) => {
                        if (!hasCurrentBirthData) return;
                        setUseCurrentPersonAsPartnerA(event.target.checked);
                      }}
                      disabled={!hasCurrentBirthData}
                    />
                    Use current chart data
                  </label>
                )}
              </div>
              <div className="interpretations-form__card-grid">
                <label className="interpretations-form__field">
                  Name
                  <input
                    type="text"
                    value={partner.name}
                    required
                    onChange={(event) => handlePartnerFieldChange(partnerKey, 'name', event.target.value)}
                    placeholder="Full name"
                  />
                </label>
                <label className="interpretations-form__field">
                  Birth date
                  <input
                    type="date"
                    value={partner.birthDate}
                    disabled={disableAutoFields}
                    required
                    onChange={(event) =>
                      handlePartnerFieldChange(partnerKey, 'birthDate', event.target.value)
                    }
                  />
                </label>
                <label className="interpretations-form__field">
                  Birth time
                  <input
                    type="time"
                    value={partner.birthTime}
                    disabled={disableAutoFields}
                    required
                    onChange={(event) =>
                      handlePartnerFieldChange(partnerKey, 'birthTime', event.target.value)
                    }
                  />
                </label>
                <label className="interpretations-form__field">
                  Birth location
                  {isPlacesLoaded && !disableAutoFields ? (
                    <Autocomplete
                      onLoad={(autocomplete) => {
                        partnerAutocompleteRefs.current[partnerKey] = autocomplete;
                      }}
                      onPlaceChanged={() => handlePartnerPlaceChanged(partnerKey)}
                      options={{
                        types: ['(cities)'],
                        fields: ['formatted_address', 'name', 'geometry'],
                      }}
                    >
                      <input
                        type="text"
                        value={partner.location}
                        required
                        onChange={(event) =>
                          handlePartnerFieldChange(partnerKey, 'location', event.target.value)
                        }
                        placeholder="City, Country"
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      type="text"
                      value={partner.location}
                      disabled={disableAutoFields}
                      required
                      onChange={(event) =>
                        handlePartnerFieldChange(partnerKey, 'location', event.target.value)
                      }
                      placeholder="City, Country"
                    />
                  )}
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {synastryFormError && <p className="form-error">{synastryFormError}</p>}
    </form>
  );

  const isTabLoading = (tabId) => {
    return loadingTabs?.[tabId] || false;
  };

  const renderActiveTab = () => {
    const tabIsLoading = isTabLoading(activeTab);

    // For planets tab, show loaded planets even while loading (progressive display)
    if (activeTab === 'planets') {
      let loadingMessage = '';
      if (tabIsLoading) {
        const loadedNames = new Set(tabContent.planets?.map(p => p.name?.toLowerCase()) || []);
      }
      return (
        <>
          {error && (
            <div className="interpretations-error">
              <strong>Note:</strong> <span>{error}</span>
            </div>
          )}
          <InterpretationAccordion
            items={tabContent.planets}
            loading={tabIsLoading}
            loadingMessage={loadingMessage}
            emptyMessage="Planetary interpretations will load automatically when chart is generated."
          />
          {user?.planTier !== 'premium' && tabContent.planets?.length > 0 && (
            <div className="mb-6 text-center">
              <p className="text-sm text-gray-500 bg-gray-50 inline-block px-4 py-2 rounded-full border border-gray-100">
                ✨ Your next free interpretation will be visible tomorrow in this section.
              </p>
            </div>
          )}
          {user?.planTier !== 'premium' && tabContent.planets?.length > 0 && (
            <div className="interpretation-card interpretation-card--locked">
              <div className="interpretation-card__content">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="bg-purple-100 p-4 rounded-full mb-4">
                    <Lock className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unlock Full Report</h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                    Get detailed interpretations for all planets, including Mercury, Venus, Mars, Jupiter, Saturn, Uranus, Neptune, and Pluto.
                  </p>
                  <button
                    onClick={() => setIsReportModalOpen(true)}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Unlock All
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      );
    }

    if (activeTab === 'dwarf') {
      return (
        <>
          {error && (
            <div className="interpretations-error">
              <strong>Note:</strong> <span>{error}</span>
            </div>
          )}
          {user?.planTier === 'premium' ? (
            <InterpretationAccordion
              items={tabContent.dwarf}
              loading={tabIsLoading}
              emptyMessage="Dwarf planet interpretations will load automatically when chart is generated."
            />
          ) : (
            <div className="interpretation-card interpretation-card--locked">
              <div className="interpretation-card__content">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="bg-purple-100 p-4 rounded-full mb-4">
                    <Lock className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unlock Dwarf Planets</h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                    Gain insights into the deeper spiritual influences of dwarf planets like Ceres, Eris, Haumea, and Makemake.
                  </p>
                  <button
                    onClick={() => setIsReportModalOpen(true)}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Unlock All
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      );
    }
    if (activeTab === 'majorAspects') {
      return (
        <>
          {error && (
            <div className="interpretations-error">
              <strong>Note:</strong> <span>{error}</span>
            </div>
          )}
          {user?.planTier === 'premium' || tabContent.majorAspects?.length > 0 ? (
            <InterpretationAccordion
              items={tabContent.majorAspects}
              loading={tabIsLoading}
              emptyMessage="Major Aspect interpretations will load automatically when chart is generated."
            />
          ) : (
            <div className="interpretation-card interpretation-card--locked">
              <div className="interpretation-card__content">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="bg-purple-100 p-4 rounded-full mb-4">
                    <Lock className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unlock Major Aspects</h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                    Free major aspect interpretations unlock after all 9 major planets are completed.
                  </p>
                  <button
                    onClick={() => setIsReportModalOpen(true)}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Unlock All
                  </button>
                </div>
              </div>
            </div>
          )}
          {user?.planTier !== 'premium' && tabContent.majorAspects?.length > 0 && (
            <div className="mb-6 text-center">
              <p className="text-sm text-gray-500 bg-gray-50 inline-block px-4 py-2 rounded-full border border-gray-100">
                ✨ Your next free aspect interpretation will be visible tomorrow in this section.
              </p>
            </div>
          )}
          {user?.planTier !== 'premium' && tabContent.majorAspects?.length > 0 && (
            <div className="interpretation-card interpretation-card--locked">
              <div className="interpretation-card__content">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="bg-purple-100 p-4 rounded-full mb-4">
                    <Lock className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unlock Major Aspects</h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                    Get detailed interpretations for all major planetary aspects in your chart.
                  </p>
                  <button
                    onClick={() => setIsReportModalOpen(true)}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Unlock All
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      );
    }

    if (activeTab === 'minorAspects') {
      return (
        <>
          {error && (
            <div className="interpretations-error">
              <strong>Note:</strong> <span>{error}</span>
            </div>
          )}
          {user?.planTier === 'premium' ? (
            <InterpretationAccordion
              items={tabContent.minorAspects}
              loading={tabIsLoading}
              emptyMessage="Major Aspect interpretations will load automatically when chart is generated."
            />
          ) : (
            <div className="interpretation-card interpretation-card--locked">
              <div className="interpretation-card__content">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="bg-purple-100 p-4 rounded-full mb-4">
                    <Lock className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unlock Minor Aspects</h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                    Dive deeper into your soul's blueprint with interpretations for minor aspects (quincunx, semi-sextile, etc.).
                  </p>
                  <button
                    onClick={() => setIsReportModalOpen(true)}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Unlock All
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      );
    }

    if (activeTab === 'transits') {
      const emptyMessage = 'Provide transit details and generate an interpretation.';

      return (
        <>
          {error && (
            <div className="interpretations-error">
              <strong>Note:</strong> <span>{error}</span>
            </div>
          )}
          {user?.planTier === 'premium' ? (
            <>
              {renderTransitForm()}
              <InterpretationAccordion
                items={tabContent.transits}
                loading={tabIsLoading}
                loadingMessage="Generating interpretations…"
                emptyMessage={emptyMessage}
              />
            </>
          ) : (
            <div className="interpretation-card interpretation-card--locked">
              <div className="interpretation-card__content">
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="bg-purple-100 p-4 rounded-full mb-4">
                    <Lock className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unlock Transit Insights</h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                    Discover how current planetary movements align with your soul's evolutionary path through personalized transit interpretations.
                  </p>
                  <button
                    onClick={() => setIsReportModalOpen(true)}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    Unlock All
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      );
    }
    // For other tabs, show round loader
    if (tabIsLoading) {
      return <RoundLoader message="Generating interpretations…" />;
    }

    if (error) {
      return (
        <div className="interpretations-error">
          <strong>Unable to load interpretations.</strong>
          <span>{error}</span>
        </div>
      );
    }

    switch (activeTab) {
      case 'synthesis':
        return (
          <>
            {user?.planTier === 'premium' ? (
              <InterpretationAccordion
                items={tabContent.synthesis}
                loading={tabIsLoading}
                emptyMessage="Your full heliocentric Overall Chart Interpretation will load automatically once generated."
              />
            ) : (
              <div className="interpretation-card interpretation-card--locked">
                <div className="interpretation-card__content">
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="bg-purple-100 p-4 rounded-full mb-4">
                  <Lock className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unlock Chart Interpretation</h3>
                  <p className="text-gray-600 mb-6 max-w-md">
                  Get a comprehensive interpretation of your soul's mission and integrative themes.
                  </p>
                    <button
                      onClick={() => setIsReportModalOpen(true)}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300"
                    >
                      Unlock All
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        );
      default:
        return null;
    }
  };

  const [pdfGenerationStatus, setPdfGenerationStatus] = useState('idle'); // 'idle', 'preparing', 'fetching'

  const generatePDF = useCallback(() => {
    const doc = new jsPDF();

    // Title
    doc.setFontSize(18);
    doc.text('Heliocentric Astrology Interpretations', 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 26);
    doc.setTextColor(0);

    let yPos = 35;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 14;
    const maxY = pageHeight - margin;

    const sections = [
      { key: 'synthesis', title: 'Overall Chart Interpretation' },
      { key: 'planets', title: 'Planetary Positions' },
      { key: 'dwarf', title: 'Dwarf Planets' },
      { key: 'aspects', title: 'Aspects' },
      { key: 'transits', title: 'Transits' },
      { key: 'synastry', title: 'Synastry' },
    ];

    sections.forEach((section) => {
      const data = tabContent[section.key];
      if (data && data.length > 0) {
        // Check if we need a new page for the section header
        if (yPos > maxY - 40) {
          doc.addPage();
          yPos = 20;
        }

        // Section Header
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(79, 70, 229); // Indigo color
        doc.text(section.title, margin, yPos);
        yPos += 8;
        doc.setTextColor(0);
        doc.setFont(undefined, 'normal');

        data.forEach((item) => {
          const title = item.title || item.name || item.id;
          const content = item.content || '';

          // Check if we need a new page for the item title
          if (yPos > maxY - 30) {
            doc.addPage();
            yPos = 20;
          }

          // Item Title
          if (title) {
            doc.setFontSize(11);
            doc.setFont(undefined, 'bold');
            doc.text(title.toUpperCase(), margin, yPos);
            yPos += 6;
          }

          // Item Content
          doc.setFontSize(10);
          doc.setFont(undefined, 'normal');

          // Split content by newlines to process headings
          const lines = content.split('\n');

          lines.forEach((line) => {
            const trimmedLine = line.trim();
            if (!trimmedLine) return;

            // Detect headings
            // Matches: ##, ###, or specific phrases like "Position & sign", "Soul meaning"
            // Case insensitive check for specific phrases
            const lowerLine = trimmedLine.toLowerCase();
            const isHeading =
              trimmedLine.startsWith('##') ||
              lowerLine.startsWith('position & sign') ||
              lowerLine.startsWith('soul meaning') ||
              lowerLine.startsWith('impact on relationships') ||
              lowerLine.startsWith('growth opportunity');

            // Clean the line (remove markdown symbols)
            const cleanLine = trimmedLine.replace(/[#*]/g, '').trim();

            if (isHeading) {
              // Ensure space for heading + at least one line of text
              if (yPos > maxY - 15) {
                doc.addPage();
                yPos = 20;
              }

              doc.setFont(undefined, 'bold');
              doc.text(cleanLine, margin, yPos);
              yPos += 5;
              doc.setFont(undefined, 'normal');
            } else {
              // Regular text
              const wrappedLines = doc.splitTextToSize(cleanLine, 180);

              wrappedLines.forEach((wLine) => {
                if (yPos > maxY - 10) {
                  doc.addPage();
                  yPos = 20;
                }
                doc.text(wLine, margin, yPos);
                yPos += 5;
              });

              // Add a small gap after paragraphs if the next line is a heading or new paragraph
              // But since we split by newline, we can just add a small spacing?
              // Actually, let's not add too much spacing to avoid whitespace issues.
            }
          });

          yPos += 4; // Spacing after item
        });

        yPos += 4; // Spacing after section
      }
    });

    doc.save('heliocentric-interpretations.pdf');
    setPdfGenerationStatus('idle');
  }, [tabContent]);

  const handleDownloadPDF = () => {
    setPdfGenerationStatus('preparing');
  };

  // Effect to trigger fetches when preparing PDF
  useEffect(() => {
    if (pdfGenerationStatus !== 'preparing') return;

    const tabsNeeded = ['planets', 'dwarf', 'aspects', 'synthesis'];
    let fetchTriggered = false;

    // Trigger fetch for missing data
    if (!loadedTabs.planets && !loadingTabs.planets) {
      onGeneratePlanetary?.(chartData, 'regular');
      fetchTriggered = true;
    }
    if (!loadedTabs.dwarf && !loadingTabs.dwarf) {
      onGeneratePlanetary?.(chartData, 'dwarf');
      fetchTriggered = true;
    }
    if (!loadedTabs.aspects && !loadingTabs.aspects) {
      onGenerateAspects?.(chartData);
      fetchTriggered = true;
    }
    if (!loadedTabs.synthesis && !loadingTabs.synthesis) {
      onGenerateSynthesis?.(chartData);
      fetchTriggered = true;
    }

    // If we triggered fetches, go to fetching state. 
    // If everything was already loaded (or loading), go to fetching state to wait for completion.
    setPdfGenerationStatus('fetching');

  }, [pdfGenerationStatus, loadedTabs, loadingTabs, chartData, onGeneratePlanetary, onGenerateAspects, onGenerateSynthesis]);

  // Effect to generate PDF when data is ready
  useEffect(() => {
    if (pdfGenerationStatus !== 'fetching') return;

    const isLoadingAny =
      loadingTabs.planets ||
      loadingTabs.dwarf ||
      loadingTabs.aspects ||
      loadingTabs.synthesis;

    if (!isLoadingAny) {
      // All done loading (or failed, but we proceed with what we have)
      // Small timeout to ensure state updates have propagated to tabContent
      const timer = setTimeout(() => {
        generatePDF();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [pdfGenerationStatus, loadingTabs, generatePDF]);

  return (
    <section className="interpretations-panel">
      <header className="interpretations-panel__header">
        <div>
          <h3>Interpretations</h3>
          <p>Guidance focusing on the soul based on Heliocentric Astrology.</p>
        </div>

        <div className="interpretations-panel__controls" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {/* Provider selection removed - using Openonly */}
          <button
            onClick={() => {
              if (!chartData) {
                const birthDataForm = document.getElementById("birth-data-form");
                if (birthDataForm) {
                  birthDataForm.scrollIntoView({ behavior: "smooth" });
                  // Optional: Focus the first input
                  const firstInput = birthDataForm.querySelector("input");
                  if (firstInput) {
                    firstInput.focus();
                  }
                }
                return;
              }
              setIsReportModalOpen(true);
            }}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2 text-sm"
          >
            {!(user?.planTier === SUBSCRIPTION_PLANS.PREMIUM) && <Lock size={14} />}
            Buy/Get Report
          </button>
        </div>
      </header>

      <nav className="interpretations-tabs">
        {TABS.map((tab) => {
          const isLoading = isTabLoading(tab.id);
          const isLoaded = loadedTabs?.[tab.id] || false;
          const requiresChart = TABS_REQUIRE_CHART.has(tab.id);
          const isDisabled = !hasChart && requiresChart;

          return (
            <button
              key={tab.id}
              type="button"
              className={activeTab === tab.id ? 'active' : ''}
              data-tab={tab.id}
              disabled={isDisabled}
              title={isDisabled ? 'Generate a chart first to access this tab.' : undefined}
              onClick={() => handleTabClick(tab.id)}
            >
              {tab.label}
              {isLoading && '...'}
              {isLoaded && !isLoading && ' ✓'}
            </button>
          );
        })}
        {/* <button
          onClick={handleDownloadPDF}
          className={`btn-generate-interpretation ${pdfGenerationStatus !== 'idle' ? 'loading' : ''}`}
          disabled={pdfGenerationStatus !== 'idle'}
          style={{
            marginLeft: 'auto',
            background: '#2563EB', // Updated background color
            fontSize: '0.85rem',
            padding: '0.45rem 1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'white',
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          {pdfGenerationStatus !== 'idle' ? 'Preparing PDF...' : 'Download PDF'}
        </button> */}
      </nav>

      <div className="interpretations-panel__content">
        {renderActiveTab()}
      </div>

      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        onPurchase={handleReportPurchase}
      />
      <Modal
        isOpen={isBirthDataErrorModalOpen}
        onClose={() => setIsBirthDataErrorModalOpen(false)}
        title="Birth Data Required"
      >
        <p>Please generate the chart before proceeding.</p>
        <div className="mt-6 flex justify-end">
          <button
            onClick={() => setIsBirthDataErrorModalOpen(false)}
            className="bg-gray-900 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            Got it
          </button>
        </div>
      </Modal>
    </section>
  );
};

InterpretationsPanel.propTypes = {
  interpretations: PropTypes.shape({
    planets: PropTypes.array,
    aspects: PropTypes.array,
    synthesis: PropTypes.oneOfType([
      PropTypes.shape({
        content: PropTypes.string,
        wordCount: PropTypes.number,
        provider: PropTypes.string,
        model: PropTypes.string,
      }),
      PropTypes.string,
    ]),
    transits: PropTypes.array,
    synastry: PropTypes.oneOfType([
      PropTypes.object,
      PropTypes.shape({
        content: PropTypes.string,
        wordCount: PropTypes.number,
        provider: PropTypes.string,
        model: PropTypes.string,
      }),
    ]),
    chartHash: PropTypes.string,
  }),
  loadingTabs: PropTypes.object,
  loadedTabs: PropTypes.object,
  chartData: PropTypes.object,
  onGeneratePlanetary: PropTypes.func,
  onGenerateAspects: PropTypes.func,
  onGenerateSynthesis: PropTypes.func,
  onGenerateTransit: PropTypes.func,
  onGenerateSynastry: PropTypes.func,
  error: PropTypes.string,
  hasChart: PropTypes.bool,
};

InterpretationsPanel.defaultProps = {
  interpretations: null,
  loadingTabs: {},
  loadedTabs: {},
  chartData: null,
  onGeneratePlanetary: null,
  onGenerateAspects: null,
  onGenerateSynthesis: null,
  onGenerateTransit: null,
  onGenerateSynastry: null,
  error: null,
  hasChart: false,
};

export default InterpretationsPanel;